import { useState, useCallback, useRef, useEffect } from 'react';
import { useAuth } from '@iamexperiences/feature-auth';
import { useTelemetry } from '@iamexperiences/ecos-telemetry';
import { OCV_APP_ID, FEEDBACK_HOST_NAME, FEEDBACK_CONTAINER_ID, FeedbackFeature} from './FeedbackConstants';
import { AppsActions } from '../../shared/Telemetry/AppsActions';
import {
  canDisplayUserFeedback,
  getTenantCloudType,
  launchFeedback,
  unmountFeedback,
  FeedbackAuthenticationType,
  FeedbackHostPlatformType,
  FeedbackUiType,
  preloadInAppFeedbackPackage,
  type FeedbackInitData,
  type IFeedbackInitOptionsWithTokenCallback,
} from '@ms/inapp-feedback-loader';
import { webLightTheme, type Theme } from '@fluentui/react-components';

const feedbackTheme: Theme = {
  ...webLightTheme,
  colorBrandBackground: webLightTheme.colorBrandBackground3Static,
  colorBrandBackgroundHover: webLightTheme.colorBrandBackground3Static,
  colorBrandBackgroundPressed: webLightTheme.colorBrandBackground3Static,
};

interface UseFeedbackOptions {
  feature: FeedbackFeature;
  enabled?: boolean;
}

interface UseFeedbackReturn {
  isSdkOpen: boolean;
  canDisplayFeedback: boolean;
  openFeedback: () => Promise<void>;
}

type LaunchFeedbackInit = Parameters<typeof launchFeedback>[1];

export function useFeedback(options: UseFeedbackOptions): UseFeedbackReturn {
  const { feature, enabled = true } = options;
  const [isSdkOpen, setIsSdkOpen] = useState(false);
  const [canDisplayFeedback, setCanDisplayFeedback] = useState(true);
  const { auth } = useAuth();
  const telemetry = useTelemetry();
  const feedbackRefId = useRef<string>(FEEDBACK_CONTAINER_ID);
  const feedbackInfoRef = useRef<FeedbackInitData | null>(null);
  const feedbackInitRef = useRef<LaunchFeedbackInit | null>(null);
  const initPromiseRef = useRef<Promise<void> | null>(null);
  const isOpeningRef = useRef(false);

  const initializeFeedback = useCallback(async () => {
    if (feedbackInfoRef.current && feedbackInitRef.current) {
      return;
    }

    if (initPromiseRef.current) {
      return initPromiseRef.current;
    }

    initPromiseRef.current = (async () => {
      const tenantId = auth.user?.tenantId;
      const userId = auth.user?.objectId;

      if (!tenantId || !userId) {
        setCanDisplayFeedback(false);
        feedbackInfoRef.current = null;
        feedbackInitRef.current = null;
        return;
      }

      const inAppFeedbackRef = document.getElementById(feedbackRefId.current);
      if (!inAppFeedbackRef) {
        setCanDisplayFeedback(false);
        feedbackInfoRef.current = null;
        feedbackInitRef.current = null;
        return;
      }

      const locale = document.documentElement.lang || 'en';

      const tenantCloudType = await getTenantCloudType({
        appId: OCV_APP_ID,
        tenantId,
        userId,
        authenticationType: FeedbackAuthenticationType.AAD,
        isProduction: true,
      });

      if (tenantCloudType === null) {
        setCanDisplayFeedback(false);
        feedbackInfoRef.current = null;
        feedbackInitRef.current = null;
        return;
      }

      const feedbackInfo: FeedbackInitData = {
        hostName: FEEDBACK_HOST_NAME,
        locale,
        inAppFeedbackRef,
      };

      const initOptions: IFeedbackInitOptionsWithTokenCallback = {
        appId: OCV_APP_ID,
        tenantId,
        userId,
        authenticationType: FeedbackAuthenticationType.AAD,
        isProduction: true,
        cloudPolicyServiceConfig: {
          callerAppHint: FEEDBACK_HOST_NAME,
        },
        feedbackConfig: {
          isDisplayed: true,
          feedbackUiType: FeedbackUiType.Modal,
          isEmailCollectionEnabled: true,
          email: auth.user?.username ?? '',
          hostPlatform: FeedbackHostPlatformType.Web,
          scenarioConfig: {
            isScenarioEnabled: false,
          },
          complianceChecks: {
            tenantCloudType,
          },
          appData: JSON.stringify({
            feature: feature,
          }),
        },
        themeOptions: { v9Theme: feedbackTheme },
        callbackFunctions: {
          getAccessToken: async (scopes, options) => {
            const scopeArray = Array.isArray(scopes) ? scopes : [scopes];
            const token = await auth.acquireToken({
              scopes: scopeArray,
              claims: options?.claims,
            });
            return { token };
          },
          onDismiss: () => {
            try {
              unmountFeedback();
            } finally {
              setIsSdkOpen(false);
            }
            telemetry.reportCustomEvent(AppsActions.feedbackModalClosed);
          },
        },
      };

      await preloadInAppFeedbackPackage(feedbackInfo);

      const { canDisplay, init } = await canDisplayUserFeedback(initOptions);
      setCanDisplayFeedback(canDisplay);

      if (!canDisplay) {
        feedbackInfoRef.current = null;
        feedbackInitRef.current = null;
        return;
      }

      feedbackInfoRef.current = feedbackInfo;
      feedbackInitRef.current = init as LaunchFeedbackInit;
    })()
      .catch(error => {
        telemetry.error(AppsActions.feedbackSdkLaunchError, error);
        setCanDisplayFeedback(false);
        feedbackInfoRef.current = null;
        feedbackInitRef.current = null;
      })
      .finally(() => {
        initPromiseRef.current = null;
      });

    return initPromiseRef.current;
  }, [auth, feature, telemetry]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    void initializeFeedback();
  }, [enabled, initializeFeedback]);

  const openFeedback = useCallback(async () => {
    if (isSdkOpen || isOpeningRef.current) {
      return;
    }

    isOpeningRef.current = true;

    try {
      await initializeFeedback();

      if (!feedbackInfoRef.current || !feedbackInitRef.current) {
        return;
      }

      await launchFeedback(feedbackInfoRef.current, feedbackInitRef.current);
      setIsSdkOpen(true);
      telemetry.reportCustomEvent(AppsActions.feedbackModalOpened);
    } catch (error) {
      telemetry.error(AppsActions.feedbackSdkLaunchError, error);
      setIsSdkOpen(false);
    } finally {
      isOpeningRef.current = false;
    }
  }, [initializeFeedback, isSdkOpen, telemetry]);

  return {
    isSdkOpen,
    canDisplayFeedback,
    openFeedback,
  };
}
